import api from "../../../api/api";

/**
 * 查询参数接口定义
 * @typedef {Object} GetSubscriptionsQuery
 * @property {number} [page=1] - 页码
 * @property {number} [limit=20] - 每页数量
 * @property {string} [name] - 组织名称
 * @property {string} [owner_email] - 拥有者邮箱
 * @property {string} [gst_no] - GST 编号
 * @property {string} [created_at_from] - 创建时间起 (YYYY-MM-DD)
 * @property {string} [created_at_to] - 创建时间止 (YYYY-MM-DD)
 * @property {string} [status] - 状态 (active, trialing, cancelled, etc.)
 */

/**
 * 获取订阅列表
 * @param {GetSubscriptionsQuery} params - 查询参数
 * @returns {Promise<AxiosResponse>}
 */
export const getSubscriptions = (params) => {
  return api.get("/root/subscriptions", { params });
};

export const getPlans = async () => {
  try {
    const res = await api.get("/subscription/getPlans");
    return res.data;
  } catch (error) {
    console.error("getPlans failed:", error);
    throw error.error;
  }
};

/**
 * 获取单个订阅的完整信息
 * @param {string} subscriptionId - 订阅 ID
 * @returns {Promise<AxiosResponse>}
 */
export const getSubscriptionDetail = (subscriptionId) => {
  return api.get(`/root/subscriptions/${subscriptionId}`);
};

/**
 * 强制更新订阅的 Body 接口定义
 * @typedef {Object} UpdateSubscriptionBody
 * @property {string} user_id - 订阅拥有者 ID (注意: 文档中称为 user_id, 实际指 owner_id)
 * @property {Object} subscription - 订阅更新对象
 * @property {string} [subscription.plan_id] - 计划 ID
 * @property {string} [subscription.status] - 状态
 * @property {boolean} [subscription.is_open_beta] - 是否开启 Open Beta (建议显式传入)
 * @property {string} reason - 操作原因 (用于审计日志)
 */

/**
 * 强制更新订阅 (Root 权限)
 * @param {UpdateSubscriptionBody} data - 请求体数据
 * @returns {Promise<AxiosResponse>}
 */
export const updateSubscription = (data) => {
  return api.post("/root/subscriptions/update", data);
};

/**
 * 重置试用期的 Body 接口定义
 * @typedef {Object} ResetTrialBody
 * @property {string} reason - 操作原因
 */

/**
 * 重置订阅为试用状态
 * @param {string} subscriptionId - 订阅 ID
 * @param {ResetTrialBody} data - 请求体数据
 * @returns {Promise<AxiosResponse>}
 */
export const resetTrial = (subscriptionId, data) => {
  return api.post(`/root/subscriptions/${subscriptionId}/reset-trial`, data);
};

/**
 * 延长试用期的 Body 接口定义
 * @typedef {Object} ExtendTrialBody
 * @property {number} [additional_days] - 额外试用天数
 * @property {number} [additionalDays] - 额外试用天数 (camelCase)
 * @property {string} reason - 操作原因
 */

/**
 * 延长订阅试用期 (Root)
 * @param {string} subscriptionId - 订阅 ID
 * @param {ExtendTrialBody} data - 请求体数据
 * @returns {Promise<AxiosResponse>}
 */
export const extendTrial = (subscriptionId, data) => {
  return api.post(
    `/root/subscriptions/${subscriptionId}/extend-trial`,
    data,
  );
};

/**
 * Shorten trial (move trial_end earlier) Body
 * @typedef {Object} ReduceTrialBody
 * @property {number} [reduction_days] - Calendar days to shorten (snake_case)
 * @property {number} [reductionDays] - Calendar days to shorten (camelCase)
 * @property {string} reason - Audit reason
 */

/**
 * Reduce subscription trial length (Root)
 * @param {string} subscriptionId - Subscription ID
 * @param {ReduceTrialBody} data - Request body
 * @returns {Promise<AxiosResponse>}
 */
export const reduceTrial = (subscriptionId, data) => {
  return api.post(
    `/root/subscriptions/${subscriptionId}/reduce-trial`,
    data,
  );
};

export const runAuditCleanup = () => {
  return api.post(
    `/root/cron/run/audit-cleanup`,
  );
};

export const runBillingEngine = () => {
  return api.post(
    `/root/cron/run/billing-engine`,
  );
};

export const runCompanyCleanup = () => {
  return api.post(
    `/root/cron/run/company-cleanup`,
  );
};
/**
 * 更新日期的 Body 接口定义
 * @typedef {Object} UpdateDatesBody
 * @property {string} [trial_end] - 试用结束日期 (YYYY-MM-DD, 可为 null)
 * @property {string} [next_billing_date] - 下次账单日期 (YYYY-MM-DD, 可为 null)
 * @property {string} reason - 操作原因
 */

/**
 * 更新试用和/或账单日期
 * @param {string} subscriptionId - 订阅 ID
 * @param {UpdateDatesBody} data - 请求体数据
 * @returns {Promise<AxiosResponse>}
 */
export const updateSubscriptionDates = (subscriptionId, data) => {
  return api.post(`/root/subscriptions/${subscriptionId}/dates`, data);
};

/**
 * 更新自定义折扣的 Body 接口定义
 * @typedef {Object} UpdateDiscountBody
 * @property {number} custom_discount_percent - 自定义折扣百分比 (建议使用 snake_case)
 * @property {string} reason - 操作原因
 */

/**
 * 更新自定义折扣
 * @param {string} subscriptionId - 订阅 ID
 * @param {UpdateDiscountBody} data - 请求体数据
 * @returns {Promise<AxiosResponse>}
 */
export const updateCustomDiscount = (subscriptionId, data) => {
  return api.post(
    `/root/subscriptions/${subscriptionId}/custom-discount`,
    data,
  );
};

/**
 * 转移拥有者的 Body 接口定义
 * @typedef {Object} TransferOwnerBody
 * @property {string} new_owner_id - 新拥有者的 User ID
 * @property {string} reason - 操作原因
 */

/**
 * 转移订阅所有权
 * @param {string} subscriptionId - 订阅 ID
 * @param {TransferOwnerBody} data - 请求体数据
 * @returns {Promise<AxiosResponse>}
 */
export const transferOwner = (subscriptionId, data) => {
  return api.post(`/root/subscriptions/${subscriptionId}/transfer-owner`, data);
};

/**
 * 获取 HitPay 只读详情
 * @param {string} subscriptionId - 订阅 ID
 * @returns {Promise<AxiosResponse>}
 */
export const getHitPayDetails = (subscriptionId) => {
  return api.get(`/root/subscriptions/${subscriptionId}/hitpay`);
};

/**
 * Root: mark unpaid subscription change logs as paid via offline transfer (bank/cheque, etc.)
 * @typedef {Object} SettleOfflinePaymentBody
 * @property {string} [reference_no] - Payment reference (bank ref, cheque no., etc.)
 * @property {string} [notes] - Optional notes
 * @property {string} [reason] - Audit reason
 */

/**
 * @param {string} subscriptionId - Subscription ID
 * @param {SettleOfflinePaymentBody} data - Request body
 * @returns {Promise<AxiosResponse>}
 */
export const settleOfflinePayment = (subscriptionId, data) => {
  return api.post(
    `/root/subscriptions/${subscriptionId}/settle-offline-payment`,
    data,
  );
};

/**
 * Owner plus sub-users for one subscription, with any open renewal invoice.
 * @param {string} subscriptionId
 */
export const getSubscriptionMembers = (subscriptionId) => {
  return api.get(`/root/subscriptions/${subscriptionId}/members`);
};

/**
 * @typedef {Object} AddSubscriptionSubUserBody
 * @property {string} name
 * @property {string} email
 * @property {string} role - viewer, editor, or admin
 * @property {string} reason
 */

/**
 * Invite or resend a sub-user, then recalculate an unpaid renewal invoice.
 * @param {string} subscriptionId
 * @param {AddSubscriptionSubUserBody} data
 */
export const addSubscriptionSubUser = (subscriptionId, data) => {
  return api.post(`/root/subscriptions/${subscriptionId}/sub-users`, data);
};

/**
 * Remove a sub-user, then recalculate an unpaid renewal invoice.
 * @param {string} subscriptionId
 * @param {string} userId
 * @param {{ reason: string }} data
 */
export const removeSubscriptionSubUser = (subscriptionId, userId, data) => {
  return api.post(
    `/root/subscriptions/${subscriptionId}/sub-users/${userId}/remove`,
    data,
  );
};

/**
 * Replace unpaid renewal invoices with one invoice for the current cycle.
 * @param {string} subscriptionId
 * @param {{ reason: string }} data
 */
export const prepareRenewCycle = (subscriptionId, data) => {
  return api.post(`/root/subscriptions/${subscriptionId}/renew-cycle`, data);
};

export const getBlockedIps = () => {
  return api.get("/root/blocked-ips");
};

export const createBlockedIp = (data) => {
  return api.post("/root/blocked-ips", data);
};

export const updateBlockedIp = (id, data) => {
  return api.post(`/root/blocked-ips/${id}/update`, data);
};

export const deleteBlockedIp = (id) => {
  return api.post(`/root/blocked-ips/${id}/delete`, {});
};
